import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  DestroyRef,
  PLATFORM_ID,
  TemplateRef,
  ViewContainerRef,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet, isPlatformBrowser } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { ConnectedPosition, ConnectionPositionPair, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';
import { TourRoteiroService } from '../../services/tour-mode/tour-roteiro.service';
import { TourService } from '../../services/tour-mode/tour.service';
import { TourNarracaoService } from '../../services/tour-mode/tour-narracao.service';
import { AiAgentService } from '../../services/API/ai-agent.service';

/** Lado do cartão em que a seta aponta o alvo — derivado da posição que o CDK escolheu. */
type LadoSeta = 'cima' | 'baixo' | 'esquerda' | 'direita';

/**
 * Ordem de preferência de ancoragem (D51): abaixo → acima → direita → esquerda. O CDK cai para a
 * próxima quando a anterior não cabe na viewport; `withPush` só corrige o resto.
 */
const POSICOES: ConnectedPosition[] = [
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 14 },
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -14 },
  { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 14 },
  { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -14 },
];

/** #2566 (2026-10-13): alvo que é/contém campo de formulário — o cartão ancora AO LADO, porque
 *  abaixo de um campo mora o próximo campo (o e-mail do cliente ficava coberto). */
const SELETOR_CONTROLE_FORM = 'input, textarea, select, [contenteditable], mat-form-field, one-input, one-select, one-textarea';
export const POSICOES_CAMPO: ConnectedPosition[] = [POSICOES[2], POSICOES[3], POSICOES[0], POSICOES[1]];

export function ehCampoDeFormulario(el: HTMLElement): boolean {
  return el.matches(SELETOR_CONTROLE_FORM) || el.querySelector(SELETOR_CONTROLE_FORM) !== null;
}

export function retangulosSeCruzam(a: DOMRect, b: DOMRect): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/** Duração da micro-confirmação "Isso!" (D53). */
const MS_CONFIRMACAO = 900;

/**
 * D51/D52/D54 — o cartão do guia.
 *
 * O balão anterior era um retângulo fixo no rodapé, sem relação nenhuma com o que estava sendo
 * ensinado: quem falava, de onde, e sobre qual elemento eram três perguntas sem resposta visual.
 * Aqui o cartão ANCORA no alvo (CDK Overlay, `flexibleConnectedTo` + seta apontando), ganha uma
 * PRESENÇA (o orbe, único elemento ousado da peça — anima só enquanto `falando()`), mostra o
 * progresso do capítulo em segmentos, pode virar um CHIP no canto sem encerrar o tour, e abre um
 * canal para perguntar (D54: o assistente que já existe, com consciência de tela — zero backend
 * novo).
 *
 * Regra de composição: uma só coisa chama atenção (o orbe). Tudo o mais usa os tokens do One
 * (`--gray-100`, `--text-100/300`, `--main-200`, `--success-200`), Nunito Sans, caixa baixa.
 */
@Component({
  selector: 'app-tour-guia',
  standalone: true,
  imports: [MatIconModule, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- Declarado FORA de qualquer @if: o TemplateRef precisa existir antes do primeiro
         alvoAtivo, senão o efeito de ancoragem não teria o que anexar no Overlay. -->
    <ng-template #cartao>
      <div
        class="tour-guia"
        role="dialog"
        aria-label="Guia do One"
        [class.canto]="!ancorado()"
        [class.ancorado]="ancorado()"
        [attr.data-seta]="ancorado() ? ladoSeta() : null"
        (mouseenter)="motor.pausarFluxo()"
        (mouseleave)="motor.retomarFluxo()"
        (focusin)="motor.pausarFluxo()"
        (focusout)="motor.retomarFluxo()"
      >
        <div class="cabecalho">
          <span class="orbe" [class.falando]="narracao.falando()" aria-hidden="true">
            <i></i><i></i><i></i>
          </span>
          <span class="nome">Guia</span>
          <span class="capitulo">{{ motor.capituloAtual()?.nome }}</span>
          <!-- D66: "Tour desta tela"/"Refazer capítulo" são um replay avulso — o rótulo avisa
               que este NÃO é o avanço da jornada completa (que segue de onde o usuário parou). -->
          @if (motor.emTrecho()) {
            <span class="trecho" data-test="trecho">desta tela</span>
          }
          <!-- O leitor de tela recebe capítulo + posição como TEXTO; a barra de segmentos ao lado
               é decoração (aria-hidden). Sem isto, o progresso existiria só em pixels. -->
          <span class="so-leitor">{{ contaAcessivel() }}</span>
          <span class="progresso" aria-hidden="true">
            @for (i of segmentos(); track i) {
              <span
                class="segmento"
                [class.atual]="i === motor.indiceNoCapitulo()"
                [class.feito]="i < motor.indiceNoCapitulo()"
              ></span>
            }
          </span>
          <button
            class="icone"
            data-test="sair"
            title="Sair do tour"
            aria-label="Sair do tour"
            (click)="motor.sair()"
          >
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <!-- aria-live SÓ nesta região (fix round 1, minor): no diálogo inteiro, qualquer troca de
             rótulo de botão, de ícone do mudo ou de segmento do progresso virava anúncio. O que o
             usuário precisa ouvir mudar é a mensagem do guia: texto, pendências e confirmação. -->
        <div class="corpo" aria-live="polite">
          <!-- textoAtual(), NUNCA passoAtual()?.texto: é o signal que troca para
               precondicao.textoAlternativo quando a pré-condição do passo resolve falso. Ligado
               no texto cru, o balão dizia "Pronto — sua empresa já pode emitir nota" enquanto a voz
               dizia "sem o certificado a nota não sai" — e quem estivesse no mudo perdia a variante
               inteira, porque nenhum outro consumidor de produção lia textoAtual. -->
          <p class="texto">{{ motor.textoAtual() }}</p>

          @if (motor.pendenciasVisiveis().length > 0) {
            <ul class="pendencias">
              @for (p of motor.pendenciasVisiveis(); track $index) { <li>{{ p }}</li> }
            </ul>
          }

          @if (confirmando()) {
            <span class="isso" data-test="isso">Isso!</span>
          }
        </div>

        <div class="acoes">
          <button
            class="icone"
            data-test="mudo"
            [title]="motor.mudo() ? 'Ativar a voz' : 'Silenciar a voz'"
            [attr.aria-label]="motor.mudo() ? 'Ativar a voz' : 'Silenciar a voz'"
            (click)="motor.alternarMudo()"
          >
            <mat-icon>{{ motor.mudo() ? 'volume_off' : 'volume_up' }}</mat-icon>
          </button>
          <button
            class="icone"
            data-test="fluxo-automatico"
            [title]="motor.fluxoAutomatico() ? 'Desligar avanço automático' : 'Ligar avanço automático'"
            [attr.aria-label]="motor.fluxoAutomatico() ? 'Desligar avanço automático' : 'Ligar avanço automático'"
            (click)="motor.alternarFluxoAutomatico()"
          >
            <mat-icon>{{ motor.fluxoAutomatico() ? 'play_circle' : 'pause_circle' }}</mat-icon>
          </button>
          <!-- D59/D63: pausar/retomar a VOZ (não o fluxo automático — botão acima) e repetir o
               passo atual; D60: voltar ao passo elegível anterior (desabilitado sem nenhum). -->
          <button
            class="icone"
            data-test="pausar"
            [attr.aria-pressed]="motor.narracaoPausada()"
            [title]="motor.narracaoPausada() ? 'Retomar a voz' : 'Pausar a voz'"
            [attr.aria-label]="motor.narracaoPausada() ? 'Retomar a voz' : 'Pausar a voz'"
            (click)="motor.narracaoPausada() ? motor.retomarNarracao() : motor.pausarNarracao()"
          >
            <mat-icon>{{ motor.narracaoPausada() ? 'play_arrow' : 'pause' }}</mat-icon>
          </button>
          <button
            class="icone"
            data-test="repetir"
            title="Repetir"
            aria-label="Repetir"
            (click)="motor.repetirPasso()"
          >
            <mat-icon>replay</mat-icon>
          </button>
          <button
            class="icone"
            data-test="voltar"
            title="Voltar"
            aria-label="Voltar"
            [disabled]="!motor.podeVoltar() || motor.transicionando()"
            (click)="motor.voltar()"
          >
            <mat-icon>arrow_back</mat-icon>
          </button>
          <button
            class="icone"
            data-test="minimizar"
            title="Minimizar"
            aria-label="Minimizar"
            (click)="minimizar()"
          >
            <mat-icon>remove</mat-icon>
          </button>
          <span class="preenche"></span>

          @if (motor.fimDeCapitulo()) {
            <span class="fim-capitulo" data-test="fim-capitulo">
              @if (motor.proximoCapitulo(); as prox) {
                <button data-test="depois" class="discreto" [disabled]="motor.transicionando()" (click)="motor.sair()">Continuar depois</button>
                <!-- seguirParaProximoCapitulo(), não proximo(): o rótulo abaixo vem de
                     proximoCapitulo(), que pula capítulos sem passo elegível, enquanto proximo()
                     avança um índice e pode pousar no fecho público do capítulo pulado. Rótulo e
                     destino têm que sair do mesmo lugar. -->
                <button data-test="seguir-capitulo" class="primario" [disabled]="motor.transicionando() || motor.telaSalvando()" (click)="motor.seguirParaProximoCapitulo()">Seguir para {{ prox.nome }}</button>
              } @else {
                <!-- D66: em trecho o rótulo encurta para "Concluir" — não há "tour inteiro" a
                     fechar, só o replay avulso desta tela/capítulo. -->
                <button data-test="concluir" class="primario" [disabled]="motor.transicionando() || motor.telaSalvando()" (click)="motor.concluir()">{{ motor.emTrecho() ? 'Concluir' : 'Concluir o tour' }}</button>
              }
            </span>
          } @else if (motor.ofereceuPular() || motor.status() !== 'aguardando') {
            <button data-test="pular" class="primario" [disabled]="motor.transicionando() || motor.telaSalvando()" (click)="aoClicarAcao()">{{ rotuloAcao() }}</button>
          }
        </div>

        <div class="perguntar">
          <input
            class="campo"
            type="text"
            data-test="pergunta"
            placeholder="Pergunte ao guia…"
            aria-label="Pergunte ao guia"
            autocomplete="off"
            [value]="pergunta()"
            (input)="aoDigitar($event)"
            (keydown.enter)="perguntar()"
          />
          <button class="icone" data-test="enviar-pergunta" title="Enviar" aria-label="Enviar pergunta" (click)="perguntar()">
            <mat-icon>send</mat-icon>
          </button>
        </div>
      </div>
    </ng-template>

    @if (motor.ativo()) {
      @if (minimizado()) {
        <button class="tour-guia-chip" data-test="chip" aria-label="Abrir o guia" (click)="expandir()">
          <span class="orbe" [class.falando]="narracao.falando()" aria-hidden="true">
            <i></i><i></i><i></i>
          </span>
          <span class="nome">Guia</span>
          <span class="mini" aria-hidden="true"><span class="feito" [style.width.%]="percentual()"></span></span>
        </button>
      } @else if (!ancorado()) {
        <ng-container [ngTemplateOutlet]="cartao" />
      }
    }
  `,
  styleUrls: ['./tour-guia.component.css'],
})
export class TourGuiaComponent {
  readonly motor = inject(TourRoteiroService);
  readonly narracao = inject(TourNarracaoService);
  private readonly tour = inject(TourService);
  private readonly ai = inject(AiAgentService);
  private readonly overlay = inject(Overlay);
  private readonly vcr = inject(ViewContainerRef);
  private readonly doc = inject(DOCUMENT);
  private readonly ehNavegador = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly cartaoTpl = viewChild<TemplateRef<unknown>>('cartao');

  /**
   * D51/D57: o elemento em realce. Mesmo insumo do farol (`alvoVisivel`, não `alvoAtivo`) — o
   * cartão nunca resolve seletor nem mede sozinho. A checagem de `isConnected` abaixo é
   * defensiva (o `ObservadorDeAlvo` de produção já garante isso antes de publicar): nunca mais
   * ancorar o CDK num elemento que saiu do DOM entre a emissão e este `computed`.
   */
  private readonly alvoBruto = toSignal(this.tour.alvoVisivel, { initialValue: null });
  private readonly alvo = computed(() => {
    const el = this.alvoBruto();
    return el && el.isConnected ? el : null;
  });

  readonly minimizado = signal(false);
  readonly pergunta = signal('');
  readonly confirmando = signal(false);
  readonly ladoSeta = signal<LadoSeta>('cima');

  /** Ancorado = há alvo E estamos no navegador (sob SSR nada de Overlay: o cartão vai ao canto). */
  readonly ancorado = computed(() => this.ehNavegador && this.alvo() !== null);

  readonly segmentos = computed(() =>
    Array.from({ length: this.motor.totalNoCapitulo() }, (_, i) => i),
  );

  readonly percentual = computed(() => {
    const total = this.motor.totalNoCapitulo();
    return total > 0 ? Math.round(((this.motor.indiceNoCapitulo() + 1) / total) * 100) : 0;
  });

  private overlayRef: OverlayRef | null = null;
  private alvoAncorado: HTMLElement | null = null;
  private assinaturaPosicao: Subscription | null = null;
  private timerConfirmacao: ReturnType<typeof setTimeout> | null = null;
  /** Estado anterior de `status()`, para detectar a BORDA de saída de 'aguardando' (D53). */
  private statusAnterior = this.motor.status();
  /** Estado anterior de `ai.aberto()`, para expandir só na borda de fechamento (D54). */
  private aiAbertoAntes = false;
  /** #2566: o chip atual veio de foco num campo coberto (não do botão) — desfaz no alvo novo. */
  private minimizadoAuto = false;
  /** Último alvo NÃO nulo: um alvo que some e volta (scroll) não é passo novo. */
  private alvoAnterior: HTMLElement | null = null;

  constructor() {
    // Ancoragem: um efeito só, com a condição inteira num lugar — qualquer mudança de alvo,
    // de minimização ou o fim do tour passa por aqui e nunca deixa Overlay pendurado.
    effect(() => {
      const alvo = this.alvo();
      const tpl = this.cartaoTpl();
      // #2566: alvo novo (passo seguinte) desfaz um minimizar AUTOMÁTICO; o manual fica.
      if (alvo && alvo !== this.alvoAnterior) {
        this.alvoAnterior = alvo;
        if (this.minimizadoAuto) this.expandir();
      }
      const mostrar = this.ehNavegador && this.motor.ativo() && !this.minimizado() && !!alvo && !!tpl;
      if (!mostrar) {
        this.desancorar();
        return;
      }
      this.ancorarEm(alvo!, tpl!);
    });

    // D53 — micro-confirmação: o passo saiu de 'aguardando' porque o usuário FEZ a coisa.
    //
    // Fix round 1 (I1): a saída de 'aguardando' também acontece quando o TOUR ACABA —
    // `encerrarUi()` (sair/concluir) põe `status` em 'ocioso'. Isso não é acerto de passo, é o
    // fim. E como este componente vive no `app.component` e sobrevive a `ativo() === false`, um
    // `confirmando` aceso aqui ficaria esperando o PRÓXIMO tour: o cartão abriria comemorando um
    // passo que ninguém fez. Por isso o gate por `ativo()` na hora do efeito, e o reset (com o
    // timer) sempre que o tour sai do ar.
    effect(() => {
      const ativo = this.motor.ativo();
      const status = this.motor.status();
      const saiuDeAguardando = this.statusAnterior === 'aguardando' && status !== 'aguardando';
      this.statusAnterior = status;
      if (!ativo) {
        this.limparConfirmacao();
        return;
      }
      if (!saiuDeAguardando || !this.ehNavegador) return;
      this.confirmando.set(true);
      if (this.timerConfirmacao !== null) clearTimeout(this.timerConfirmacao);
      this.timerConfirmacao = setTimeout(() => {
        this.timerConfirmacao = null;
        this.confirmando.set(false);
      }, MS_CONFIRMACAO);
    });

    // D54 — o cartão volta ao FECHAR o assistente. Detecção por BORDA (true → false): um
    // `minimizar()` manual do usuário, com o assistente sempre fechado, não pode ser desfeito
    // por este efeito no primeiro flush.
    effect(() => {
      const aberto = this.ai.aberto();
      if (this.aiAbertoAntes && !aberto) this.expandir();
      this.aiAbertoAntes = aberto;
    });

    // #2566: captura (true) para ver o foco antes de qualquer stopPropagation da tela.
    if (this.ehNavegador) this.doc.addEventListener('focusin', this.aoFocarCampo, true);
    inject(DestroyRef).onDestroy(() => {
      if (this.ehNavegador) this.doc.removeEventListener('focusin', this.aoFocarCampo, true);
      this.desancorar();
      this.limparConfirmacao();
    });
  }

  /** #2566: foco num campo que o cartão ancorado está cobrindo ⇒ vira chip (sai da frente). */
  private readonly aoFocarCampo = (ev: FocusEvent): void => {
    const painel = this.overlayRef?.overlayElement;
    if (!painel || this.minimizado()) return;
    const campo = ev.target;
    if (!(campo instanceof HTMLElement) || painel.contains(campo) || !ehCampoDeFormulario(campo)) return;
    if (!retangulosSeCruzam(campo.getBoundingClientRect(), painel.getBoundingClientRect())) return;
    this.minimizadoAuto = true;
    this.minimizado.set(true);
  };

  /** Apaga a micro-confirmação e o timer dela — fim de tour e destruição usam o mesmo ponto. */
  private limparConfirmacao(): void {
    if (this.timerConfirmacao !== null) clearTimeout(this.timerConfirmacao);
    this.timerConfirmacao = null;
    this.confirmando.set(false);
  }

  /** Capítulo + posição em texto, para quem ouve a tela (a barra de segmentos é decoração). */
  contaAcessivel(): string {
    const posicao = `${this.motor.indiceNoCapitulo() + 1} de ${this.motor.totalNoCapitulo()}`;
    const cap = this.motor.capituloAtual();
    return cap ? `${cap.nome} — ${posicao}` : posicao;
  }

  minimizar(): void {
    this.minimizado.set(true);
  }

  expandir(): void {
    this.minimizadoAuto = false;
    this.minimizado.set(false);
  }

  aoDigitar(evento: Event): void {
    this.pergunta.set((evento.target as HTMLInputElement).value);
  }

  /**
   * D54 — a dúvida vira conversa com o assistente que já existe (com consciência de tela), e o
   * guia sai da frente enquanto isso. Nada de backend novo, nada de segundo chat.
   */
  perguntar(): void {
    const texto = this.pergunta().trim();
    if (!texto) return;
    this.pergunta.set('');
    this.minimizado.set(true);
    this.ai.abrir();
    // Fire-and-forget de propósito: o assistente já mostra erro na própria conversa, e uma
    // rejeição aqui não pode derrubar o tour.
    void this.ai.converseTexto(texto);
  }

  /**
   * "Não tenho agora" declara que pular é resposta legítima; "Continuar" sugere que se espera algo.
   */
  rotuloAcao(): string {
    if (this.motor.passoAtual()?.opcional) return 'Não tenho agora';
    return this.motor.status() === 'aguardando' ? 'Pular este passo' : 'Continuar';
  }

  /**
   * O botão único de ação muda de sentido conforme `ofereceuPular`: quando o motor OFERECEU
   * a saída de emergência, o clique é essa saída (`pular`); fora disso é só "avançar
   * manualmente" (`proximo`). A chave é `ofereceuPular`, não `status`, porque no motor real
   * `_ofereceuPular` só liga depois do timeout dentro de `iniciarEspera` — e essa função é a
   * única que também põe `status` em `aguardando` — logo `ofereceuPular() === true` já implica
   * `status() === 'aguardando'` na prática; testar por `ofereceuPular` é o sinal mais direto de
   * "a saída foi oferecida" e não exige duplicar a mesma checagem de estado em dois lugares.
   */
  aoClicarAcao(): void {
    if (this.motor.ofereceuPular()) {
      void this.motor.pular();
    } else {
      void this.motor.proximo();
    }
  }

  private ancorarEm(alvo: HTMLElement, tpl: TemplateRef<unknown>): void {
    if (this.overlayRef && this.alvoAncorado === alvo) return;
    // Alvo novo: recria em vez de remendar a estratégia — `flexibleConnectedTo` fixa a origem na
    // criação, e um OverlayRef reaproveitado carregaria a origem do passo anterior.
    this.desancorar();

    const posicao = this.overlay
      .position()
      .flexibleConnectedTo(alvo)
      .withPositions(ehCampoDeFormulario(alvo) ? POSICOES_CAMPO : POSICOES)
      .withViewportMargin(12)
      .withPush(true);

    this.assinaturaPosicao = posicao.positionChanges.subscribe((mudanca) =>
      this.ladoSeta.set(ladoDaPosicao(mudanca.connectionPair)),
    );

    this.overlayRef = this.overlay.create({
      positionStrategy: posicao,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      panelClass: 'tour-guia-panel',
    });
    this.overlayRef.attach(new TemplatePortal(tpl, this.vcr));
    this.alvoAncorado = alvo;
  }

  private desancorar(): void {
    this.assinaturaPosicao?.unsubscribe();
    this.assinaturaPosicao = null;
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.alvoAncorado = null;
  }
}

/** De que lado do cartão fica o alvo, para a seta apontar para ele. */
function ladoDaPosicao(par: ConnectionPositionPair): LadoSeta {
  if (par.overlayY === 'top' && par.originY === 'bottom') return 'cima';
  if (par.overlayY === 'bottom' && par.originY === 'top') return 'baixo';
  return par.overlayX === 'end' ? 'direita' : 'esquerda';
}
